/**
 * KautionMagic — Öffentliche Freigabe-Seite (kein Login nötig).
 * Der Eigentümer erhält eine URL /kaution-freigabe/{token} per E-Mail und kann
 * die Kautionsabrechnung dort einsehen, kommentieren und freigeben/ablehnen.
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { CheckCircle, XCircle, ShieldCheck } from "@phosphor-icons/react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;
const fmtEUR = (v) => new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(Number(v || 0));
const fmtDate = (iso) => { if (!iso) return "—"; try { return new Date(iso).toLocaleDateString("de-DE"); } catch { return iso; } };

export default function KautionMagic() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [approverName, setApproverName] = useState("");
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/kaution-magic/${token}`)
      .then(({ data }) => setData(data))
      .catch((e) => setError(e?.response?.data?.detail || "Link nicht mehr gültig"));
  }, [token]);

  const approve = async () => {
    if (!approverName.trim()) { toast.error("Bitte Name angeben"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/api/kaution-magic/${token}/approve`, { approver_name: approverName, comment: comment || null });
      setDone("approved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); }
    finally { setBusy(false); }
  };

  const reject = async () => {
    if (!approverName.trim()) { toast.error("Bitte Name angeben"); return; }
    if (!comment.trim()) { toast.error("Bitte Ablehnungsgrund als Kommentar angeben"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/api/kaution-magic/${token}/reject`, { approver_name: approverName, comment });
      setDone("rejected");
    } catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); }
    finally { setBusy(false); }
  };

  if (error) return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50">
      <div className="max-w-md bg-white border border-rose-200 rounded-md p-6 text-center">
        <XCircle size={40} className="text-rose-600 mx-auto mb-2" />
        <div className="font-semibold text-rose-800">Link ungültig</div>
        <div className="text-sm text-slate-500 mt-1">{error}</div>
      </div>
    </div>
  );

  if (!data) return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50">
      <div className="text-slate-400">Lade Freigabe-Anfrage…</div>
    </div>
  );

  const already = data.already_approved || data.already_rejected;
  const s = data.settlement || {};

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4">
      <div className="max-w-2xl mx-auto space-y-4">
        <div className="text-center">
          <ShieldCheck size={40} className="text-emerald-600 mx-auto mb-2" />
          <h1 className="text-2xl font-semibold">Kautionsabrechnung – Freigabe</h1>
          <p className="text-sm text-slate-500 mt-1">Bitte prüfen Sie die untenstehende Abrechnung und geben Sie diese frei oder lehnen Sie sie ab.</p>
        </div>

        <div className="bg-white border border-slate-200 rounded-md p-5 space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCell label="Ende MV" value={fmtDate(s.end_of_tenancy_date)} />
            <StatCell label="Kapital" value={fmtEUR(s.principal_total)} />
            <StatCell label="Zinsen § 551" value={fmtEUR(s.interest_total)} />
            <StatCell label="Abzüge" value={fmtEUR(s.deductions_total)} />
          </div>
          <div className="text-center pt-2">
            <div className="text-xs uppercase tracking-wider text-slate-500">Netto-Auszahlung an Mieter</div>
            <div className="text-2xl font-bold text-emerald-700">{fmtEUR(s.net_return)}</div>
          </div>

          {s.deductions?.length > 0 && (
            <div className="border-t pt-2">
              <div className="text-xs font-semibold text-slate-700 mb-1">Abzüge im Detail</div>
              <ul className="text-sm space-y-1">
                {s.deductions.map((d, i) => (
                  <li key={i} className="flex justify-between border-b border-slate-100 py-1">
                    <span>{d.description} <span className="text-slate-400 text-xs">({d.category})</span></span>
                    <span className="tabular-nums">{fmtEUR(d.amount)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {s.interest_lines?.length > 0 && (
            <details className="border-t pt-2">
              <summary className="text-xs font-semibold cursor-pointer text-slate-700">Zinsberechnung anzeigen ({s.interest_lines.length} Zeilen)</summary>
              <table className="min-w-full text-xs mt-2">
                <thead className="bg-slate-50">
                  <tr className="text-left text-slate-500">
                    <th className="px-2 py-1">Jahr</th>
                    <th className="px-2 py-1 text-right">Kapital</th>
                    <th className="px-2 py-1 text-right">Tage</th>
                    <th className="px-2 py-1 text-right">Zins %</th>
                    <th className="px-2 py-1 text-right">Zins</th>
                  </tr>
                </thead>
                <tbody>
                  {s.interest_lines.map((l, i) => (
                    <tr key={i} className="border-t border-slate-100">
                      <td className="px-2 py-1">{l.year}</td>
                      <td className="px-2 py-1 text-right tabular-nums">{fmtEUR(l.principal)}</td>
                      <td className="px-2 py-1 text-right tabular-nums">{l.days}/{l.days_basis}</td>
                      <td className="px-2 py-1 text-right tabular-nums">{Number(l.rate_pct).toFixed(2)}</td>
                      <td className="px-2 py-1 text-right tabular-nums font-medium">{fmtEUR(l.interest)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </details>
          )}
        </div>

        {done === "approved" && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-md p-4 text-center">
            <CheckCircle size={32} className="text-emerald-600 mx-auto mb-1" />
            <div className="font-semibold text-emerald-800">Freigegeben</div>
            <div className="text-sm text-slate-600 mt-1">Vielen Dank für Ihre Freigabe.</div>
          </div>
        )}
        {done === "rejected" && (
          <div className="bg-rose-50 border border-rose-200 rounded-md p-4 text-center">
            <XCircle size={32} className="text-rose-600 mx-auto mb-1" />
            <div className="font-semibold text-rose-800">Abgelehnt</div>
            <div className="text-sm text-slate-600 mt-1">Die Verwaltung wurde informiert.</div>
          </div>
        )}
        {already && !done && (
          <div className="bg-slate-100 border border-slate-200 rounded-md p-4 text-center text-slate-700">
            Diese Anfrage wurde bereits bearbeitet (Status: {s.status}).
          </div>
        )}

        {!already && !done && (
          <div className="bg-white border border-slate-200 rounded-md p-5 space-y-3">
            <div>
              <label className="text-xs font-semibold text-slate-700">Ihr Name *</label>
              <Input value={approverName} onChange={(e) => setApproverName(e.target.value)} placeholder="Vor- und Nachname" data-testid="magic-approver-name" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-700">Kommentar (optional bei Freigabe, Pflicht bei Ablehnung)</label>
              <Textarea rows={3} value={comment} onChange={(e) => setComment(e.target.value)} data-testid="magic-comment" />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={reject} disabled={busy} className="text-rose-700 border-rose-300 gap-1" data-testid="magic-reject">
                <XCircle size={16} /> Ablehnen
              </Button>
              <Button onClick={approve} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 gap-1" data-testid="magic-approve">
                <CheckCircle size={16} /> Freigeben
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function StatCell({ label, value }) {
  return (
    <div className="border border-slate-200 bg-slate-50 rounded p-2">
      <div className="text-[10px] uppercase text-slate-500">{label}</div>
      <div className="font-semibold tabular-nums text-sm">{value}</div>
    </div>
  );
}
